import t from "@/lang/lang";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { useState } from "react";
import { Label } from "@/shared/components/ui/label";
import { DocumentId } from "@bitory-gmbh/bsm";
import { NullUUID } from "@bitory-gmbh/nidavellir";
import { DocumentNumberSelect } from "@/services/backend/numbers/components/document-number-select";
import { useNumbersDocumentNumberShowQuery } from "@/services/backend/numbers/api/document-number-api";
import { OutboxDocumentReference } from "../types/outbox";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";

export function OutboxDocumentReferencesCard({
  outbox,
  onOutboxChange,
  disabled,
}: {
  outbox: { documents: OutboxDocumentReference[] };
  onOutboxChange: (documents: { documents: OutboxDocumentReference[] }) => void;
  disabled?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("GSM Dokumente")}</CardTitle>
        <CardDescription>
          {t("Auf diese Dokumente bezieht sich die Übermittlung.")}
        </CardDescription>
        <CardAction>
          <AddDocumentReferenceDialog
            outbox={outbox}
            onOutboxChange={onOutboxChange}
            disabled={disabled}
          />
        </CardAction>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Numer")}</TableHead>
              <TableHead className="text-right">{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {outbox.documents.length === 0 && <TableEmptyRow colSpan={2} />}
            {outbox.documents.map((doc) => (
              <TableRow key={doc.outboxDocumentId}>
                <TableCell>
                  <DocumentNumberById documentId={doc.outboxDocumentId} />
                </TableCell>
                <TableCell className="text-right">
                  <DeleteDocumentReferenceDialog
                    outboxDocumentReference={doc}
                    outbox={outbox}
                    onOutboxChange={onOutboxChange}
                    disabled={disabled}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function AddDocumentReferenceDialog({
  outbox,
  onOutboxChange,
  disabled,
}: {
  outbox: { documents: OutboxDocumentReference[] };
  onOutboxChange: (documents: { documents: OutboxDocumentReference[] }) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<DocumentId>();
  const { data: documentNumber } = useNumbersDocumentNumberShowQuery(
    { documentId: selected ?? NullUUID },
    { skip: !selected || selected === "" },
  );

  const doAdd = () => {
    if (!selected || !documentNumber) return;

    onOutboxChange({
      ...outbox,
      documents: [
        ...outbox.documents,
        {
          outboxDocumentId: documentNumber.documentId,
          outboxDocumentKind: documentNumber.documentKind,
          outboxDocumentNumber: documentNumber.string,
          outboxDocumentSubdomain: documentNumber.serviceKind.toLowerCase(),
        },
      ],
    });

    setSelected(undefined);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild disabled={disabled}>
        <Button size="sm">
          <Plus />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Dokument Suchen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Hier können sie manuell eine Referenz zu einem Dokument erfassen",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid content-start gap-1.5">
          <Label>{t("Dokument")}</Label>
          <DocumentNumberSelect
            value={selected}
            onValueChange={setSelected}
            documentKind={{ active: true, values: [GSMDocumentKind.WorkOrder] }}
            serviceKind={{
              active: true,
              values: [GSMServiceKind.VBS, GSMServiceKind.RLT],
            }}
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doAdd}>{t("Hinzufügen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDocumentReferenceDialog({
  outboxDocumentReference,
  outbox,
  onOutboxChange,
  disabled,
}: {
  outboxDocumentReference: OutboxDocumentReference;
  outbox: { documents: OutboxDocumentReference[] };
  onOutboxChange: (documents: { documents: OutboxDocumentReference[] }) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const doDelete = () => {
    onOutboxChange({
      ...outbox,
      documents: outbox.documents.filter(
        (doc) =>
          doc.outboxDocumentId !== outboxDocumentReference.outboxDocumentId,
      ),
    });

    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild disabled={disabled}>
        <Button size="sm" variant="destructive">
          <Trash2 />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Dokument Entfernen")}</DialogTitle>
          <DialogDescription>
            {t(
              `Möchten Sie die Referenz zum Dokument ${outboxDocumentReference.outboxDocumentNumber} wirklich entfernen?`,
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDelete} variant="destructive">
            {t("Entfernen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
